import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { TextareaInput } from "@/components/ui/Field";
import { toast } from "@/lib/toast";

export interface HtmlTemplateEditorProps {
  title: string;
  description?: string;
  load: () => Promise<string>;
  save: (html: string) => Promise<void>;
  onReset?: () => Promise<void>;
  resetLabel?: string;
  extraControls?: React.ReactNode;
}

export function HtmlTemplateEditor({
  title,
  description,
  load,
  save,
  onReset,
  resetLabel = "Auf Standard zurücksetzen",
  extraControls,
}: HtmlTemplateEditorProps) {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setHtml(await load());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Vorlage konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [load]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function handleSave() {
    setSaving(true);
    try {
      await save(html);
      toast.success("Gespeichert.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    if (!onReset) return;
    if (!window.confirm("Vorlage wirklich zurücksetzen?")) return;
    setSaving(true);
    try {
      await onReset();
      await reload();
      toast.success("Zurückgesetzt.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Zurücksetzen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title={title}
      description={description}
      actions={
        <div className="flex flex-wrap gap-2">
          {onReset && (
            <Button type="button" variant="secondary" onClick={handleReset} disabled={saving || loading}>
              {resetLabel}
            </Button>
          )}
          <Button type="button" onClick={handleSave} disabled={saving || loading}>
            {saving ? "Speichern …" : "Speichern"}
          </Button>
        </div>
      }
    >
      {extraControls}
      {loading && (
        <div className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Lädt …
        </div>
      )}
      {!loading && (
        <TextareaInput
          label="HTML"
          value={html}
          onChange={(e) => setHtml(e.target.value)}
          rows={18}
          className="font-mono text-xs"
        />
      )}
    </Card>
  );
}
